import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { FullscreenLoading, useToastr } from '@farmshare/ui-components';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useMatches } from 'react-router-dom';

import { getApolloClient } from 'lib/graphql/client';
import { useAuthStorageCleanup } from 'lib/hooks';
import { isPlaywrightTestMode } from 'lib/test-mode';
import { useCognitoAuth } from 'features/auth';
import {
  Box,
  Button,
  Collapse,
  Container,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';

interface AuthApolloProviderProps {
  children: ReactNode;
}

interface ErrorDetailsProps {
  graphQLErrors?: readonly any[];
  networkError?: any;
}

function ErrorDetails({ graphQLErrors, networkError }: ErrorDetailsProps) {
  const [showDetails, setShowDetails] = useState(false);

  // Collect all error messages
  const errorMessages: { error: string; type: string }[] = [];

  if (graphQLErrors && graphQLErrors.length > 0) {
    graphQLErrors.forEach((error) => {
      if (error.message) {
        errorMessages.push({ error: error.message, type: 'System Error' });
      }
    });
  }

  if (networkError && networkError.message) {
    errorMessages.push({ error: networkError.message, type: 'Network Error' });
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        An unexpected error occurred while processing your request.
      </Typography>

      <Button
        variant="text"
        startIcon={<ArrowDropDown />}
        color="inherit"
        size="small"
        onClick={() => setShowDetails(!showDetails)}
        sx={{ mt: 1 }}
      >
        {showDetails ? 'Hide Details' : 'Show Details'}
      </Button>

      <Collapse in={showDetails}>
        <List sx={{ mt: 1 }}>
          {errorMessages.map((message, index) => (
            <ListItem key={index} disablePadding>
              <ListItemText
                primary={message.error}
                secondary={message.type}
                slotProps={{
                  primary: { variant: 'body1', fontSize: '0.9rem' },
                  secondary: { variant: 'subtitle1', fontSize: '0.75rem' },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  );
}

export function AuthApolloProvider({ children }: AuthApolloProviderProps) {
  const isTestMode = isPlaywrightTestMode();

  // Use Cognito auth for all authentication
  const cognitoAuth = useCognitoAuth();
  const { push } = useToastr();
  const location = useLocation();
  const matches = useMatches();

  const [accessToken, setAccessToken] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [apolloClient, setApolloClient] =
    useState<ApolloClient<NormalizedCacheObject> | null>(null);

  const { cleanupApolloCache, checkAndClearLogoutMarker, markLogout } =
    useAuthStorageCleanup();
  const previousTokenRef = useRef<string | undefined>();
  const hasInitializedRef = useRef<boolean>(false);

  // Keep a mutable ref for route info so the auth middleware always sends
  // current headers without needing to recreate the Apollo client on every
  // navigation.
  const routeInfoRef = useRef<{ pathname: string; routePattern?: string }>({
    pathname: location.pathname,
  });

  // Build route pattern by replacing param values with param names
  const cleanRoutePattern = (() => {
    // Get the last match (most specific route)
    const lastMatch = matches[matches.length - 1];
    if (!lastMatch?.params || !lastMatch?.pathname) {
      return location.pathname;
    }

    let pattern = lastMatch.pathname;

    // Replace each param value with :paramName
    Object.entries(lastMatch.params).forEach(([key, value]) => {
      if (value) {
        // Escape special regex characters in the value
        const escapedValue = String(value).replace(
          /[.*+?^${}()|[\]\\]/g,
          '\\$&',
        );
        pattern = pattern.replace(new RegExp(escapedValue, 'g'), `:${key}`);
      }
    });

    return pattern;
  })();

  // Always keep routeInfoRef current so headers reflect the latest route.
  routeInfoRef.current = {
    pathname: location.pathname,
    routePattern: cleanRoutePattern,
  };

  // Create error callback for GraphQL errors
  const handleGraphQLError = ({
    graphQLErrors,
    networkError,
  }: {
    graphQLErrors?: readonly any[];
    networkError?: any;
  }) => {
    push({
      title: 'Your request could not be completed',
      body: (
        <ErrorDetails
          graphQLErrors={graphQLErrors}
          networkError={networkError}
        />
      ),
      severity: 'error',
      delay: 10000,
      removeOnNextPush: true,
    });
  };

  // Clean up Apollo cache persist data when auth token changes (after initialization)
  useEffect(() => {
    // Skip if still loading
    if (loading) {
      return;
    }

    // Mark as initialized after first run
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      previousTokenRef.current = accessToken;
      return;
    }

    // Check if token has changed
    const tokenChanged = previousTokenRef.current !== accessToken;

    if (tokenChanged) {
      cleanupApolloCache();
      previousTokenRef.current = accessToken;
    }
  }, [accessToken, loading, cleanupApolloCache]);

  // Check for logout marker when authentication is established (handles logout/login flow)
  useEffect(() => {
    if (!loading && cognitoAuth?.isAuthenticated && accessToken) {
      checkAndClearLogoutMarker();
    }
  }, [
    loading,
    cognitoAuth?.isAuthenticated,
    accessToken,
    checkAndClearLogoutMarker,
  ]);

  if (!import.meta.env.PROD) {
    loadDevMessages();
    loadErrorMessages();
  }

  // Check for Cognito auth token in localStorage
  const getCognitoToken = (): string | null => {
    try {
      const stored = localStorage.getItem('cognito_auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.accessToken || null;
      }
    } catch (_error) {
      // Invalid storage data
    }
    return null;
  };

  const updateAccessToken = (token: string) => {
    const decodedToken: JwtPayload = jwtDecode(token);
    if (decodedToken?.exp && decodedToken.exp * 1000 < Date.now()) {
      markLogout();
      cognitoAuth?.logout({ preserveRedirect: true });
      return;
    }

    setAccessToken(token);
  };

  useEffect(() => {
    if (cognitoAuth?.isLoading) return;

    // In test mode, use mock token directly — no Cognito tokens exist
    if (isTestMode) {
      setAccessToken('mock_test_token');
      setLoading(false);
      return;
    }

    // Check for Cognito token
    const cognitoToken = getCognitoToken();
    if (cognitoToken) {
      // eslint-disable-next-line no-console
      console.log('[AuthApolloProvider] Using Cognito token');
      updateAccessToken(cognitoToken);
      setLoading(false);
      return;
    }

    // No valid token found
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cognitoAuth?.isAuthenticated, cognitoAuth?.isLoading]);

  useEffect(() => {
    if (loading) return; // Skip if still loading access token

    let isMounted = true;

    const initializeApolloClient = async () => {
      try {
        const client = await getApolloClient(
          cognitoAuth?.accessToken || '',
          handleGraphQLError,
          // Pass a getter so the auth middleware always reads the current route
          // without requiring the Apollo client to be recreated on navigation.
          () => routeInfoRef.current,
          cognitoAuth?.impersonateUserId,
          {
            checkTokenExpiry: () =>
              cognitoAuth?.checkTokenExpiry(cognitoAuth?.idToken) ?? true,
            refreshToken: async () => {
              // Trigger token refresh via Cognito auth context
              if (!cognitoAuth?.refreshAccessToken) {
                throw new Error('Token refresh not available');
              }
              return cognitoAuth.refreshAccessToken();
            },
          },
        );
        if (isMounted) {
          setApolloClient(client);
        }
      } catch (_error) {
        // Failed to initialize Apollo client
        if (isMounted) {
          setApolloClient(null);
        }
      }
    };

    initializeApolloClient();

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    cognitoAuth?.impersonateUserId,
    cognitoAuth?.idToken,
    cognitoAuth?.accessToken,
  ]);

  // If we're still loading the access token, Auth0 state, or Apollo client, show a loading indicator
  if (loading || !apolloClient) {
    return (
      <Container>
        <FullscreenLoading />
      </Container>
    );
  }

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}
